import React, { useState, useEffect } from 'react'
import { Button, Select, Tag, message as antdMessage, Dropdown, Modal, Tooltip } from 'antd'
import { Slider } from 'antd'
import { Switch } from 'antd'
import useSpeechTranslator from '@/hooks/useSpeechTranslator'
import styles from './index.module.scss'
import type { videoTalkRefProps } from '@/components/VideoTalk/index.d'
import intl from 'react-intl-universal'
import { useAppSelector } from '@/store/hooks'
import { AI_TRANSLATE_LANG_MAP } from '@/config/index'
import { AudioOutlined, DownOutlined } from "@ant-design/icons";
import { SettingOutlined } from "@ant-design/icons";

export type LocaleKeysType = keyof typeof AI_TRANSLATE_LANG_MAP
export type TranslationControlProps = {
  videoTalkRef?: React.RefObject<videoTalkRefProps>
  className?: string
  style?: React.CSSProperties
  onStart?: () => void
  onStop?: () => void
  onResult?: (result: { text: string, translation?: string, isFinal?: boolean }) => void
  initLanguageFrom?: string
  initLanguageTo?: string
  changeLanguage?: boolean
}

const TranslationControl: React.FC<TranslationControlProps> = ({
  videoTalkRef,
  className,
  style,
  onStart,
  onStop,
  onResult,
  initLanguageFrom,
  initLanguageTo,
  changeLanguage = true,
}) => {
  const intlOptions = intl.getInitOptions()
  const currentLocale = intlOptions.currentLocale || ''
  // 根据全局语言切换默认的 languageFrom：
  // - 当全局为中文（如 'zh-CN'）时，识别源设为英文 'en-US'
  // - 当全局为英文（如 'en-US'）时，识别源设为中文 'zh-CN'
  const isZh = String(currentLocale).toLowerCase().startsWith('zh')
  const [languageFrom, setLanguageFrom] = useState<string>(initLanguageFrom ?? currentLocale)
  const [languageTo, setLanguageTo] = useState<string>(initLanguageTo ?? (isZh ? 'en-US' : 'zh-CN'))
  const [active, setActive] = useState(false)
  const [starting, setStarting] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const translator = useSpeechTranslator()
  // 设置项相关 state
  const [enableTrueText, setEnableTrueText] = useState(true)
  const [segmentationTimeoutMs, setSegmentationTimeoutMs] = useState(500) // 单位：毫秒
  // phraseList用于语音识别SDK（string[]），phraseDict用于翻译前自定义词典
  const [phraseDict, setPhraseDict] = useState<Record<string, string>>({
    // 'Kira South': 'Kira South',
    // 'New Zealand': '新西兰',
  })
  const [phraseList, setPhraseList] = useState<string[]>(Object.keys(phraseDict))
  const [newPhrase, setNewPhrase] = useState('')
  const [newPhraseTarget, setNewPhraseTarget] = useState('')

  // 公用添加短语方法
  const handleAddPhrase = () => {
    if (newPhrase.trim() && newPhraseTarget.trim()) {
      const newDict = { ...phraseDict, [newPhrase.trim()]: newPhraseTarget.trim() }
      setPhraseDict(newDict)
      setPhraseList(Object.keys(newDict))
      setNewPhrase('')
      setNewPhraseTarget('')
    }
  }

  const menuItemStyle = { minWidth: "120px" }
  // ...existing code...

  const items = Object.keys(AI_TRANSLATE_LANG_MAP).map((key) => {
    const localeObj = AI_TRANSLATE_LANG_MAP[key as LocaleKeysType]
    return {
      key: localeObj.lang,
      label: localeObj.label,
      style: menuItemStyle,
    }
  })
  const waitForLocalMedia = async (timeout = 3000, interval = 200): Promise<MediaStream | null> => {
    const start = Date.now()
    while (Date.now() - start < timeout) {
      try {
        const fn = videoTalkRef?.current?.getLocalMediaStream
        const s = fn ? fn.call(videoTalkRef?.current, true) as MediaStream : null
        if (s) return s
      } catch (e) {
        console.warn('error while checking local media', e)
        return null
      }
      // small delay
      // eslint-disable-next-line no-await-in-loop
      await new Promise(res => setTimeout(res, interval))
    }
    return null
  }

  const handleStart = async () => {
    setStarting(true)
    try {
      const localMedia = await waitForLocalMedia(3000, 200)
      console.log('TranslationControl localMedia', localMedia)
      // 替换短语为唯一ID，翻译后再反向替换为目标词
      const phraseKeys = Object.keys(phraseDict)
      const phraseIdMap: Record<string, string> = {}
      phraseKeys.forEach((phrase, idx) => {
        phraseIdMap[phrase] = `__PHRASE_${idx}__`
      })
      // 替换为ID
      const replacePhraseWithId = (text: string) => {
        let result = text
        phraseKeys.forEach(phrase => {
          result = result.replace(new RegExp(phrase, 'g'), phraseIdMap[phrase])
        })
        return result
      }
      // 翻译后反向替换为目标词
      const restorePhraseFromId = (text: string) => {
        let result = text
        phraseKeys.forEach(phrase => {
          result = result.replace(new RegExp(phraseIdMap[phrase], 'g'), phraseDict[phrase])
        })
        return result
      }
      console.error('languageTo', languageTo)
      const started = await translator.start({
        source: localMedia ? 'trtc' : 'microphone',
        sourceStream: localMedia || undefined,
        languageFrom: languageFrom,
        languageTo: languageTo,
        enableTrueText,
        segmentationTimeoutMs,
        phraseList,
      }, async (r) => {
        let result = {
          ...r,
          text: r.text || '',
          translation: r.translation || '',
          isFinal: r.isFinal || false,
        }
        // 英文环境且phraseList不为空，text中包含phraseList短语时，翻译前用自定义词典替换
        if (
          String(languageFrom).toLowerCase().startsWith('en') &&
          Object.keys(phraseDict).length > 0 &&
          result.text && Object.keys(phraseDict).some(phrase => result.text.includes(phrase)) &&
          result.isFinal
        ) {
          try {
            const processedText = replacePhraseWithId(result.text)
            const reTrans = await translator.translateText({
              text: processedText,
              languageFrom: languageFrom,
              languageTo: languageTo,
            })
            console.log('字典中 === 原始text ===', result.text)
            console.log('字典中 === 替换为ID后 ===', processedText)
            console.log('字典中 === 翻译文案 ===', reTrans.translation)
            if (reTrans && reTrans.translation) {
              result.translation = restorePhraseFromId(reTrans.translation)
            }
          } catch (e) {
            // 可选：错误处理
          }
        } else {
          // console.log('原始text ===', result.text)
        }
        console.log('实时翻译结果：', result)
        onResult && onResult(result)
      })
      if (started) {
        setActive(true)
        onStart && onStart()
        setStarting(false)
      } else {
        setStarting(false)
        antdMessage?.warning(intl.get('translation.start_failed') || '无法开启语音翻译')
      }
    } catch (err) {
      console.error('translator.start error', err)
      setStarting(false)
      antdMessage?.error(intl.get('translation.error') || '语音翻译出错')
    }
  }

  // 卸载时尝试停止翻译以释放设备
  useEffect(() => {
    // 开启自动翻译
    if (active || starting || translator.status === 'running') return
    handleStart()
    return () => {
      try {
        translator.stop()
      } catch (e) {
        // ignore
      }
    }
  }, [])
  useEffect(() => {
    // 切换目标语言时，重启识别并强制重新翻译当前草稿内容（如有）
    const restart = async () => {
      try {
        setActive(false)
        setStarting(false)
        await translator.stop()
        await handleStart()
      } catch (e) {
        antdMessage?.error(intl.get('translation.switch_fail') || '目标语言切换失败，请重试')
      }
    }
    restart()
  }, [languageTo])

  return (
    <div className={[styles.container, className || ''].join(' ')} style={style}>
      <div className={styles.controls}>
        {
          changeLanguage &&
          <div className={styles.target_language}>
            <span className={styles.target_language_tip}>{intl.get('translation.language_to')}: </span>
            <Dropdown
              trigger={["click"]}
              menu={{
                items,
                onClick: ({ key }) => {
                  const newLang = key
                  console.log('Dropdown === newLang', newLang)
                  setLanguageTo(newLang)
                },
                selectedKeys: [languageTo]
              }}>
              <div className={styles.select_lang}>
                <span className={styles.select_item}>
                  {AI_TRANSLATE_LANG_MAP[languageTo as LocaleKeysType]?.label}
                </span>
                <DownOutlined className={styles.select_icon} />
              </div>
            </Dropdown>
          </div>
        }
        {
          !active || translator.status === 'starting' &&
          <Tag color="blue" style={{marginRight:0}}>{intl.get('translation.starting')}</Tag>
        }
        {
          translator.status === 'running' &&
          <Tag color="green" style={{marginRight:0}}>
            <AudioOutlined />
            {intl.get('translation.tag_running')}
          </Tag>
        }
        {
          translator.status === 'error' &&
          <Tag color="red" style={{marginRight:0}}>{intl.get('translation.tag_error')}</Tag>
        }
        {
          active && translator.status === 'running' &&
          <Tooltip title={intl.get('translation.tooltip_settings')} placement="topRight">
            <SettingOutlined
              style={{ fontSize: 16, cursor: 'pointer', color: 'rgba(0, 0, 0, 0.4)', paddingRight:12,}}
              onClick={() => setShowSettings(true)}
            />
          </Tooltip>
        }
      </div>
      {/* 设置弹窗 */}
      <Modal
        title={intl.get('translation.settings')}
        open={showSettings}
        onCancel={() => setShowSettings(false)}
        footer={[
          <Button 
            key="ok"
            type="primary"
            onClick={async () => {
              setShowSettings(false)
              try {
                setActive(false)
                setStarting(false)
                await translator.stop()
                handleStart()
              } catch(e) {}
            }}
          >
            {intl.get('common.i_see') || '确定'}
          </Button>
        ]}
        centered
      >
        <div>
          {/* 填充词开关 */}
          <div style={{display:'flex',alignItems:'center',marginBottom:16}}>
            <span style={{flex:1}}>{intl.get('translation.remove_filler')}</span>
            <Switch checked={enableTrueText} onChange={setEnableTrueText} />
          </div>
          {/* 语音分割停顿设置 */}
          <div style={{marginBottom:16}}>
            <label style={{display:'block',marginBottom:5}}>
              {intl.get('translation.sentence_pause')}: <span style={{fontWeight:'bold',color:'#4caf50'}}>
                {segmentationTimeoutMs <= 300 ? intl.get('translation.sentence_pause_fast') : segmentationTimeoutMs <= 1000 ? intl.get('translation.sentence_pause_normal') : intl.get('translation.sentence_pause_slow')} ({segmentationTimeoutMs}ms)
              </span>
            </label>
            <div style={{width:'100%'}}>
              <Slider
                min={200}
                max={2000}
                step={10}
                value={segmentationTimeoutMs}
                onChange={setSegmentationTimeoutMs}
                style={{width:'100%',verticalAlign:'middle',marginLeft:0,marginRight:0}}
                tooltip={{formatter: v => v !== undefined ? `${v}ms` : ''}}
              />
              <div style={{display:'flex',justifyContent:'space-between',fontSize:11,color:'#999',width:'100%',marginTop:2,marginLeft:0,marginRight:0}}>
                <span>{intl.get('translation.sentence_pause_fast')} (200ms)</span>
                <span>{intl.get('translation.sentence_pause_normal')} (500ms)</span>
                <span>{intl.get('translation.sentence_pause_slow')} (2000ms)</span>
              </div>
              <small style={{color:'#666',display:'block',marginTop:5,width:'100%',marginLeft:0,marginRight:0}}>
                {intl.get('translation.sentence_pause_desc')}
              </small>
            </div>
          </div>
          {/* 短语列表设置，仅英语时显示 */}
          {String(languageFrom).toLowerCase().startsWith('en') && (
            <div style={{marginBottom:16}}>
              <div style={{fontWeight:500,marginBottom:5}}>{intl.get('translation.phrase_list_title')}</div>
              <div style={{display:'flex',flexWrap:'wrap',gap:8,marginBottom:8}}>
                {Object.entries(phraseDict).map(([phrase, target], idx) => (
                  <Tag
                    key={phrase+idx}
                    closable
                    onClose={() => {
                      const newDict = { ...phraseDict }
                      delete newDict[phrase]
                      setPhraseDict(newDict)
                      setPhraseList(Object.keys(newDict))
                    }}
                    style={{marginRight:0}}
                  >{phrase} → {target}</Tag>
                ))}
              </div>
              <div style={{display:'flex',gap:8}}>
                <input
                  type="text"
                  value={newPhrase}
                  onChange={e => setNewPhrase(e.target.value)}
                  placeholder={intl.get('translation.phrase_list_placeholder')}
                  style={{flex:2,minWidth:0,padding:'4px 8px',border:'1px solid #ddd',borderRadius:4}}
                />
                <input
                  type="text"
                  value={newPhraseTarget}
                  onChange={e => setNewPhraseTarget(e.target.value)}
                  placeholder={intl.get('translation.phrase_list_target_placeholder')}
                  style={{flex:2,minWidth:0,padding:'4px 8px',border:'1px solid #ddd',borderRadius:4}}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      handleAddPhrase()
                    }
                  }}
                />
                <Button
                  type="primary"
                  disabled={!newPhrase.trim() || !newPhraseTarget.trim()}
                  onClick={handleAddPhrase}
                >{intl.get('translation.phrase_list_add')}</Button>
              </div>
              <small style={{color:'#666',display:'block',marginTop:5}}>
                {intl.get('translation.phrase_list_desc')}
              </small>
            </div>
          )}
        </div>
      </Modal>
    </div>
  )
}

export default TranslationControl
